/**
 * @file Header
 * @todo Refactor to use Class, similar to mobile-nav.js
 */

import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { ScrollToPlugin } from 'gsap/ScrollToPlugin';

/**
 * Header component
 */
const vHeader = () => {
	const header = document.querySelector( '.v-header' );

	if ( ! header ) {
		return;
	}

	gsap.registerPlugin( ScrollTrigger );
	gsap.registerPlugin( ScrollToPlugin );

	const mainNav        = header.querySelector( '.v-header__nav' );
	const subnavTriggers = header.querySelectorAll( '.v-header__nav-link--btn' );
	const scrollCta      = header.querySelector( '.v-header__cta--scroll' );
	const animations     = {};
	const openNavs       = {};
	const earlyTrackOpen = {};
	let openSliding      = false;

	/**
	 * Menu Open Cleanup
	 *
	 * @param {HTMLDivElement} subnav main nav trigger button
	 * @param {NodeList} subnavItems subnav menu items
	 * @param {number} index index of the trigger within all triggers
	 */
	const menuOpenCleanup = ( subnav, subnavItems, index ) => {
		subnav.classList.add( 'v-header__nav-dropdown--open' );
		subnav.removeAttribute( 'style' );
		[ ...subnavItems ].forEach( ( item ) => item.removeAttribute( 'style' ) );
		animations[ index ] = false;
		openNavs[ index ]   = true;
	};

	/**
	 * Menu Open Animation
	 *
	 * @param {HTMLButtonElement} trigger main nav trigger button
	 * @param {HTMLDivElement} subnav subnav wrapper
	 * @param {NodeList} subnavItems subnav menu items
	 * @param {number} index index of the trigger within all triggers
	 */
	const openAnimation = ( trigger, subnav, subnavItems, index ) => {
		gsap.timeline( {
			defaults: {
				ease:     'sine.out',
				duration: .3,
			},
			onStart() {
				trigger.setAttribute( 'aria-expanded', 'true' );
			},
			onComplete() {
				menuOpenCleanup( subnav, subnavItems, index );
			},
		} )
			.fromTo( subnav, { opacity: 0, scaleX: 0 }, {
				opacity: 1,
				scaleX:  1,
			} )
			.fromTo( subnav, { scaleY: 0 }, {
				scaleY: 1,
			}, '<+.1' )
			.fromTo( subnavItems, { opacity: 0, y: -10 }, {
				opacity: 1,
				y:       0,
				stagger: .075,
			} );
	};

	/**
	 * Menu Close Cleanup
	 *
	 * @param {HTMLButtonElement} trigger main nav trigger button
	 * @param {HTMLDivElement} subnav main nav trigger button
	 * @param {NodeList} subnavItems subnav menu items
	 * @param {number} index index of the trigger within all triggers
	 */
	const menuCloseCleanup = ( trigger, subnav, subnavItems, index ) => {
		subnav.classList.remove( 'v-header__nav-dropdown--open' );
		subnav.removeAttribute( 'style' );
		[ ...subnavItems ].forEach( ( item ) => item.removeAttribute( 'style' ) );
		trigger.setAttribute( 'aria-expanded', 'false' );
		animations[ index ] = false;
		openNavs[ index ]   = false;
	};

	/**
	 * Menu Close Animation
	 *
	 * @param {HTMLButtonElement} trigger main nav trigger button
	 * @param {HTMLDivElement} subnav subnav wrapper
	 * @param {NodeList} subnavItems subnav menu items
	 * @param {number} index index of the trigger within all triggers
	 */
	const closeAnimation = ( trigger, subnav, subnavItems, index ) => {
		gsap.timeline( {
			defaults: {
				ease:     'sine.in',
				duration: .2,
			},
			onComplete() {
				menuCloseCleanup( trigger, subnav, subnavItems, index );
			},
		} )
			.fromTo( subnavItems, { opacity: 1, y: 0 }, {
				opacity: 0,
				y:       -10,
				stagger: -.05,
			} )
			.fromTo( subnav, { scaleY: 1 }, {
				scaleY: 0,
			} )
			.fromTo( subnav, { opacity: 1, scaleX: 1 }, {
				opacity: 0,
				scaleX:  0,
			}, '<+.0667' );
	};

	[ ...subnavTriggers ].forEach( ( trigger, index ) => {
		const subnav            = trigger.parentNode.querySelector( '.v-header__nav-dropdown' );
		const subnavItems       = subnav.querySelectorAll( '.v-header__nav-child' );
		earlyTrackOpen[ index ] = false;
		openNavs[ index ]       = false;

		trigger.addEventListener( 'click', () => {
			const otherAnimation = Object.values( animations ).filter( ( anim ) => anim ).length;

			if ( otherAnimation || openSliding ) {
				return;
			}

			const openNav = Object.keys( openNavs )
				.filter( ( key ) => openNavs[ key ] && parseInt( key ) !== index );

			if ( openNav.length ) {
				openSliding              = true;
				const currentTrigger     = subnavTriggers[ openNav[ 0 ] ];
				const currentSubnav      = currentTrigger.parentNode.querySelector( '.v-header__nav-dropdown' );
				const currentSubnavItems = currentSubnav.querySelectorAll( '.v-header__nav-child' );
				const currentBg          = currentSubnav.querySelector( '.v-header__nav-dropdown-bg' );
				const currentTriangle    = currentSubnav.querySelector( '.v-header__nav-dropdown-triangle' );
				const triangle           = subnav.querySelector( '.v-header__nav-dropdown-triangle' );

				gsap.set( subnav, {
					display: 'block',
					scale:   1,
				} );
				const subnavLayout        = subnav.getBoundingClientRect();
				const currentSubnavLayout = currentSubnav.getBoundingClientRect();

				const triangleLayout        = triangle.getBoundingClientRect();
				const currentTriangleLayout = currentTriangle.getBoundingClientRect();

				const scaleX = index > parseInt( openNav[ 0 ] )
					? ( ( subnavLayout.x + subnavLayout.width - currentSubnavLayout.x ) / currentSubnavLayout.width )
					: ( ( currentSubnavLayout.x + currentSubnavLayout.width - subnavLayout.x ) / currentSubnavLayout.width );
				const scaleY = subnavLayout.height / currentSubnavLayout.height;

				gsap.set( currentBg, {
					x:               0,
					scaleX:          1,
					scaleY:          1,
					transformOrigin: `${ index > parseInt( openNav[ 0 ] ) ? 'left' : 'right' } top`,
				} );

				gsap.set( currentTriangle, {
					x: 0,
				} );

				gsap.timeline( {
					defaults: {
						ease:          'sine.out',
						repeatRefresh: true,
					},
					repeatRefresh: true,
					onStart() {
						trigger.setAttribute( 'aria-expanded', 'true' );
						earlyTrackOpen[ index ] = true;
					},
					onComplete() {
						menuOpenCleanup( subnav, subnavItems, index );
						openSliding = false;
					},
				} )
					.to( currentSubnavItems, {
						opacity:  0,
						y:        -10,
						stagger:  -.075,
						duration: .15,
					} )
					.to( currentBg, {
						scaleY,
						duration: .4,
						onUpdate() {
							const x                      =  gsap.getProperty( currentBg, 'scaleX' );
							const y                      =  gsap.getProperty( currentBg, 'scaleY' );
							currentBg.style.borderRadius = `${ 8 / x }px / ${ 8 / y }px`;
						},
					} )
					.to( currentBg, {
						scaleX,
						duration: .15,
						onUpdate() {
							const x                      =  gsap.getProperty( currentBg, 'scaleX' );
							const y                      =  gsap.getProperty( currentBg, 'scaleY' );
							currentBg.style.borderRadius = `${ 8 / x }px / ${ 8 / y }px`;
						},
						onComplete() {
							gsap.set( currentBg, {
								transformOrigin: `${ index > parseInt( openNav[ 0 ] ) ? 'right' : 'left' } top`,
								x:               subnavLayout.x - currentSubnavLayout.x,
							} );
						},
					}, '<' )
					.to( currentTriangle, {
						x:        triangleLayout.x - currentTriangleLayout.x,
						duration: .3,
					}, '<' )
					.to( currentBg, {
						scaleX:   subnavLayout.width / currentSubnavLayout.width,
						duration: .25,
						onUpdate() {
							const x                      =  gsap.getProperty( currentBg, 'scaleX' );
							const y                      =  gsap.getProperty( currentBg, 'scaleY' );
							currentBg.style.borderRadius = `${ 8 / x }px / ${ 8 / y }px`;
						},
						onComplete() {
							menuCloseCleanup( currentTrigger, currentSubnav, currentSubnavItems, openNav[ 0 ] );
							currentBg.removeAttribute( 'style' );
							currentTriangle.removeAttribute( 'style' );
							earlyTrackOpen[ openNav[ 0 ] ] = false;

							subnav.style.opacity = 1;
						},
					}, '>-.15' )
					.to( subnavItems, {
						opacity:  1,
						y:        0,
						stagger:  .075,
						duration: .3,
					} );

				return;
			}

			animations[ index ] = true;
			if ( earlyTrackOpen[ index ] ) {
				earlyTrackOpen[ index ] = false;
				closeAnimation( trigger, subnav, subnavItems, index );
			} else {
				earlyTrackOpen[ index ] = true;
				subnav.style.display    = 'block';
				openAnimation( trigger, subnav, subnavItems, index );
			}
		} );
	} );

	document.addEventListener( 'click', ( e ) => {
		if ( ! mainNav.contains( e.target ) && Object.values( openNavs ).filter( ( anim ) => anim ).length ) {
			const otherAnimation = Object.values( animations ).filter( ( anim ) => anim ).length;

			if ( otherAnimation || openSliding ) {
				return;
			}

			const openNav = Object.keys( openNavs )
				.filter( ( key ) => openNavs[ key ] );

			const currentTrigger     = subnavTriggers[ openNav[ 0 ] ];
			const currentSubnav      = currentTrigger.parentNode.querySelector( '.v-header__nav-dropdown' );
			const currentSubnavItems = currentSubnav.querySelectorAll( '.v-header__nav-child' );

			earlyTrackOpen[ openNav[ 0 ] ] = false;
			closeAnimation( currentTrigger, currentSubnav, currentSubnavItems, openNav[ 0 ] );
		}
	} );

	const bg       = header.querySelector( '.v-header__bg' );
	const logoLink = header.querySelector( '.v-header__logo-link' );
	const logo     = header.querySelector( '.v-header__logo' );

	let logoWidth = 0;
	/**
	 * Set logo width
	 *
	 * Stored to calculate animations, updates on window resize
	 */
	const setLogoWidth = () => {
		logoWidth = logo.getBoundingClientRect().width;
	};
	setLogoWidth();
	window.addEventListener( 'resize', setLogoWidth );

	const bpSmall  = matchMedia( '(max-width: 550px)' );
	const bpMedium = matchMedia( '(max-width: 840px)' ); // was previously 768px
	const bpLarge  = matchMedia( '(max-width: 1500px)' ); // was previosuly 1400px
	/**
	 * Get current breakpoint size
	 *
	 * @return {string} breakpoint group
	 */
	const getBreakpoint = () => {
		if ( bpSmall.matches ) {
			return 'small';
		} else if ( bpMedium.matches ) {
			return 'medium';
		} else if ( bpLarge.matches ) {
			return 'large';
		}

		return 'full';
	};

	const navTrigger = header.querySelector( '.v-header__nav-trigger' );
	const cta        = header.querySelector( '.v-header__cta' );

	const headerAnimation = gsap.timeline( {
		defaults: {
			ease:     'sine.out',
			duration: .5,
		},
		onComplete: () => {
			header.classList.add( 'v-header--stuck' );
			navTrigger.removeAttribute( 'style' );
			logo.removeAttribute( 'style' );
			mainNav.removeAttribute( 'style' );

			if ( cta ) {
				cta.removeAttribute( 'style' );
			}
		},
		onReverseComplete: () => {
			header.classList.remove( 'v-header--stuck' );
			navTrigger.removeAttribute( 'style' );
			logo.removeAttribute( 'style' );
			mainNav.removeAttribute( 'style' );

			if ( cta ) {
				cta.removeAttribute( 'style' );
			}
		},
		scrollTrigger: {
			trigger: '.v-header-track',
			start:   'top top',
			// toggleActions: 'play none none reverse',
			onEnter: () => {
				setTimeout( () => headerAnimation.timeScale( 1.5 ), 0 );
			},
			onLeaveBack: () => {
				setTimeout( () => headerAnimation.timeScale( -2.5 ), 0 );
			},
		},
	} )
		.to( bg, {
			opacity: 1,
		} )
		.to( '.v-header__logo-words, .v-header__logo-reg', {
			opacity: 0,
		}, '<' )
		.to( logoLink, {
			scaleX:   .158730159,
			duration: 1,
			ease:     'sine.inOut',
			onUpdate() {
				const currentWindow = getBreakpoint();
				const currentScale  = gsap.getProperty( logoLink, 'scaleX' );

				let targetLogoY;
				switch ( currentWindow ) {
					case 'large' :
						targetLogoY = 2;
						break;
					case 'medium' :
						targetLogoY = 1;
						break;
					case 'small' :
						targetLogoY = 2;
						break;
					default :
						targetLogoY = 3;
				}

				navTrigger.style.transform = `translateY(${ ( currentWindow === 'small' ? 2 : 0 ) * this.progress() }px)`;
				logo.style.transform       = `translateY(${ targetLogoY * this.progress() }px) scaleX(${ 1 / currentScale })`;
				mainNav.style.transform    = `translate(${ ( logoWidth - ( logoWidth * currentScale ) ) * -1 }px, ${ ( currentWindow === 'large' ? -6 : -4 ) * this.progress() }px)`;

				if ( cta ) {
					cta.style.transform        = `translateY(${ ( currentWindow === 'small' ? -1 : 0 ) * this.progress() }px)`;
				}
			},
		}, '<+.25' );

	if ( ! scrollCta ) {
		return;
	}

	scrollCta.addEventListener( 'click', ( e ) => {
		e.preventDefault();
		const { target } = e;
		const { hash }   = target;
		const { top }    = document.querySelector( hash ).getBoundingClientRect();
		const windowY    = window.scrollY;

		gsap.to( window, {
			duration: 1,
			ease:     'power2.inOut',
			scrollTo: {
				y: parseInt( top + windowY, 10 ),
			},
		} );
	} );
};

export default vHeader;
